import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { getAreaBySlug, LONDON_AREAS, type LondonArea } from "@/lib/london-areas";
import { SERVICES } from "@/lib/services-catalog";
import { breadcrumb, faqPage } from "@/lib/seo-jsonld";
import { ArrowRight, MapPin, ShieldCheck, Train } from "lucide-react";

export const Route = createFileRoute("/london/$area")({
  loader: ({ params }) => {
    const area = getAreaBySlug(params.area);
    if (!area) throw notFound();
    return { area };
  },
  head: ({ loaderData, params }) => {
    const area = loaderData?.area;
    const name = area?.name ?? params.area;
    const title = `Trusted Helpers in ${name} — Handyman, TV Mounting, Assembly & Moving | Peopels.com`;
    const description = area
      ? `Vetted local Helpers in ${name} (${area.postcodes.join(", ")}). Handyman, TV mounting, furniture assembly and moving help — paid in escrow.`
      : `Vetted local Helpers in ${name}.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `/london/${params.area}` },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `/london/${params.area}` }],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold">Area not found</h1>
        <p className="mt-2 text-muted-foreground">We couldn't find that London area page.</p>
        <Link to="/london" className="mt-4 inline-block text-primary underline">Browse all London areas</Link>
      </main>
      <Footer />
    </div>
  ),
  errorComponent: () => (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold">Something went wrong</h1>
        <Link to="/london" className="mt-4 inline-block text-primary underline">Back to London</Link>
      </main>
      <Footer />
    </div>
  ),
  component: AreaPage,
});

function AreaPage() {
  const { area } = Route.useLoaderData() as { area: LondonArea };
  const neighbours = area.neighbours
    .map((slug: string) => LONDON_AREAS.find((a) => a.slug === slug))
    .filter((a): a is LondonArea => !!a);

  const faqs = [
    {
      q: `Which postcodes does Peopels.com cover in ${area.name}?`,
      a: `We cover ${area.postcodes.join(", ")} and the surrounding ${area.borough} postcodes. Just enter your postcode when posting a job and we'll only show Helpers who serve your street.`,
    },
    {
      q: `How quickly will a Helper accept my ${area.name} job?`,
      a: `Most ${area.name} jobs are matched within a few minutes. Urgent jobs are prioritised to the closest available, top-rated Helpers.`,
    },
    {
      q: `Are ${area.name} Helpers vetted?`,
      a: `Yes. Every Helper passes ID verification and KYC checks before they can take work, and we hold every payment in escrow until you confirm the job is done.`,
    },
    {
      q: `How much do Helpers in ${area.name} charge?`,
      a: `Hourly rates in ${area.name} typically range from £25 to £70 depending on the service. You'll see a clear estimate before you book and only pay after the job is complete.`,
    },
    {
      q: `What about ${neighbours[0]?.name ?? "neighbouring areas"}?`,
      a: `Many ${area.name} Helpers also cover ${neighbours.slice(0, 3).map((n) => n.name).join(", ")}. If we don't have one free in ${area.name} right away, we'll match you with a top-rated Helper in a neighbouring area.`,
    },
  ];

  const url = `/london/${area.slug}`;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <Breadcrumbs items={[{ label: "Greater London", to: "/london" }, { label: area.name }]} />

        <header className="mb-8">
          <p className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-primary">
            <MapPin className="h-3.5 w-3.5" /> {area.borough} · {area.postcodes.join(" · ")}
          </p>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-5xl">
            Trusted Helpers in {area.name}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
            {area.intro} Peopels.com matches you with vetted local Helpers in {area.name} for handyman work, TV mounting, furniture assembly and moving help.
          </p>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {area.housing}
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1"><ShieldCheck className="h-3 w-3 text-primary" /> ID-verified</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1">Escrow payments</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1"><Train className="h-3 w-3 text-primary" /> Near {area.tubeOrRail[0]}</span>
          </div>
        </header>

        <section aria-labelledby="services-h" className="mb-12">
          <h2 id="services-h" className="font-display text-2xl font-semibold">Helper services in {area.name}</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/london/$area/$service"
                  params={{ area: area.slug, service: s.slug }}
                  className="block rounded-2xl border border-border bg-card p-5 shadow-card transition hover:border-primary/40"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold">{s.name} in {area.name}</h3>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{s.summary}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    From <span className="font-semibold text-foreground">£{s.rateLow}/hr</span> · typically {Math.round(s.avgDurationMinutes / 60 * 10) / 10} hr
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="why-h" className="mb-12 rounded-2xl border border-border bg-card p-6">
          <h2 id="why-h" className="font-display text-xl font-semibold">Why book a Helper in {area.name} with Peopels.com</h2>
          <ul className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
            <li>• Vetted local Helpers who know {area.name} streets and access</li>
            <li>• Transparent estimates before you book — no surprise call-out fees</li>
            <li>• Stripe escrow payments — Helper only paid once you confirm done</li>
            <li>• Card authorized at booking — funds only released when the job is complete</li>
            <li>• Real reviews from {area.name} and {area.borough} customers</li>
            <li>• Same-day availability for most small jobs</li>
          </ul>
        </section>

        <section aria-labelledby="landmarks-h" className="mb-12">
          <h2 id="landmarks-h" className="font-display text-xl font-semibold">Around {area.name}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We work across {area.name} and the surrounding streets — from {area.landmarks.slice(0, 2).join(" and ")} to{" "}
            {area.landmarks.slice(2).join(", ") || area.tubeOrRail.join(", ")}. Helpers travel by public transport from{" "}
            {area.tubeOrRail.join(", ")} or by van for moving jobs.
          </p>
        </section>

        <section aria-labelledby="near-h" className="mb-12">
          <h2 id="near-h" className="font-display text-xl font-semibold">Helpers in nearby areas</h2>
          <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {neighbours.map((n: LondonArea) => (
              <li key={n.slug}>
                <Link
                  to="/london/$area"
                  params={{ area: n.slug }}
                  className="block rounded-xl border border-border bg-card px-3 py-2.5 text-sm transition hover:border-primary/40"
                >
                  <span className="block font-medium">{n.name}</span>
                  <span className="block text-[11px] text-muted-foreground">{n.postcodes.join(" · ")}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="faq-h" className="mb-12">
          <h2 id="faq-h" className="font-display text-2xl font-semibold">{area.name} — frequently asked</h2>
          <dl className="mt-4 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-4 open:shadow-card">
                <summary className="cursor-pointer list-none text-sm font-medium">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </dl>
        </section>

        <section className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-card to-card p-6 text-center shadow-card">
          <h2 className="font-display text-xl font-semibold">Need a Helper in {area.name}?</h2>
          <p className="mt-1 text-sm text-muted-foreground">Free to post, and you'll see estimates before you pay.</p>
          <Link
            to="/post-job"
            className="mt-4 inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Post a job in {area.name}
          </Link>
        </section>

        {/* JSON-LD */}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              breadcrumb([
                { name: "Home", path: "/" },
                { name: "Greater London", path: "/london" },
                { name: area.name, path: url },
              ]),
            ),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPage(faqs)) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: `Peopels.com Helpers in ${area.name}`,
              url,
              areaServed: {
                "@type": "Place",
                name: `${area.name}, ${area.borough}, London`,
                address: { "@type": "PostalAddress", addressLocality: area.name, addressRegion: "London", postalCode: area.postcodes[0], addressCountry: "GB" },
              },
              priceRange: "££",
              description: `Vetted local Helpers in ${area.name} for handyman, TV mounting, furniture assembly and moving help.`,
            }),
          }}
        />
      </main>
      <Footer />
    </div>
  );
}
